import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/SeriesComplete.css';

function SeriesComplete() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [challenge, setChallenge] = useState(null);
  const [games, setGames] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChallengeResults();
  }, [challengeId]);

  const fetchChallengeResults = async () => {
    try {
      setLoading(true);
      const [challengeRes, gamesRes, progressRes] = await Promise.all([
        api.get(`/challenges/${challengeId}`),
        api.get(`/challenges/${challengeId}/games`),
        api.get(`/challenges/${challengeId}/progress`)
      ]);

      setChallenge(challengeRes.data);
      setGames(gamesRes.data);
      setProgress(progressRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnHome = () => {
    navigate('/challenges');
  };

  if (loading) {
    return <div className="series-complete-container"><p>Loading results...</p></div>;
  }

  if (error) {
    return <div className="series-complete-container"><p className="error">{error}</p></div>;
  }

  if (!challenge || !progress) {
    return <div className="series-complete-container"><p>No results found</p></div>;
  }

  const isCreator = user.id === challenge.creator_id;
  const playerName = isCreator ? challenge.creator_username : challenge.opponent_username;
  const opponentName = isCreator ? challenge.opponent_username : challenge.creator_username;
  const playerWins = isCreator ? progress.creator_wins : progress.opponent_wins;
  const opponentWins = isCreator ? progress.opponent_wins : progress.creator_wins;
  
  let result = 'TIE';
  let resultClass = 'tie';
  let resultMessage = 'Series Tied!';

  if (playerWins > opponentWins) {
    result = 'WIN';
    resultClass = 'win';
    resultMessage = `You won the series!`;
  } else if (playerWins < opponentWins) {
    result = 'LOSS';
    resultClass = 'loss';
    resultMessage = `${opponentName} won the series!`;
  }

  const totalWager = challenge.wager * challenge.num_games;
  const grossPot = totalWager * 2;
  const chargePercent = Number.isFinite(Number(challenge.charge_percent)) ? Number(challenge.charge_percent) : 0;
  const chargeAmount = Number.isFinite(Number(challenge.charge_amount))
    ? Number(challenge.charge_amount)
    : Math.floor(grossPot * (chargePercent / 100));
  const winnerPayout = Number.isFinite(Number(challenge.net_pot))
    ? Number(challenge.net_pot)
    : Math.max(0, grossPot - chargeAmount);
  const payoutMap = challenge?.prize_distribution && typeof challenge.prize_distribution === 'object'
    ? challenge.prize_distribution
    : {};

  const creatorPayoutFromMap = Number(payoutMap?.[challenge.creator_id] ?? payoutMap?.[String(challenge.creator_id)] ?? 0);
  const opponentPayoutFromMap = Number(payoutMap?.[challenge.opponent_id] ?? payoutMap?.[String(challenge.opponent_id)] ?? 0);

  const fallbackCreatorPayout = playerWins > opponentWins
    ? winnerPayout
    : playerWins < opponentWins
      ? 0
      : Math.floor(winnerPayout / 2);
  const fallbackOpponentPayout = playerWins < opponentWins
    ? winnerPayout
    : playerWins > opponentWins
      ? 0
      : winnerPayout - Math.floor(winnerPayout / 2);

  const creatorPayout = creatorPayoutFromMap > 0 || opponentPayoutFromMap > 0
    ? creatorPayoutFromMap
    : fallbackCreatorPayout;
  const opponentPayout = creatorPayoutFromMap > 0 || opponentPayoutFromMap > 0
    ? opponentPayoutFromMap
    : fallbackOpponentPayout;

  const yourPayout = isCreator ? creatorPayout : opponentPayout;
  const profitLoss = yourPayout - totalWager;

  return (
    <div className="series-complete-container">
      <div className="results-card">
        <div className={`result-header ${resultClass}`}>
          <h1>Series Complete</h1>
          <div className={`result-badge ${resultClass}`}>{result}</div>
        </div>

        <div className="result-message">
          <h2>{resultMessage}</h2>
        </div>

        <div className="series-stats">
          <div className="stat-box player-stat">
            <div className="stat-name">{playerName}</div>
            <div className="stat-number wins">{playerWins}</div>
            <div className="stat-label">Wins</div>
          </div>

          <div className="stat-box">
            <div className="stat-number">{progress.ties}</div>
            <div className="stat-label">Ties</div>
          </div>

          <div className="stat-box opponent-stat">
            <div className="stat-name">{opponentName}</div>
            <div className="stat-number">{opponentWins}</div>
            <div className="stat-label">Wins</div>
          </div>
        </div>

        <div className="financial-summary">
          <div className="summary-row">
            <span>Games Played:</span>
            <strong>{progress.games_played}/{challenge.num_games}</strong>
          </div>
          <div className="summary-row">
            <span>Wager per game:</span>
            <strong>{challenge.wager} wikipenny</strong>
          </div>
          <div className="summary-row">
            <span>Total at Risk:</span>
            <strong>{totalWager} wikipenny</strong>
          </div>
          <div className="summary-row">
            <span>Winner payout (after {chargePercent}% fee):</span>
            <strong>{winnerPayout} wikipenny</strong>
          </div>
          <div className="summary-row">
            <span>Your payout:</span>
            <strong>{yourPayout} wikipenny</strong>
          </div>
          <div className="summary-divider"></div>
          <div className={`summary-row profit-loss ${profitLoss > 0 ? 'gain' : profitLoss < 0 ? 'loss' : 'neutral'}`}>
            <span>Profit/Loss:</span>
            <strong>
              {profitLoss > 0 ? '+' : ''}{profitLoss} wikipenny
            </strong>
          </div>
        </div>

        <div className="games-breakdown">
          <h3>Game Results</h3>
          <div className="games-list">
            {games.map((game, index) => (
              <div key={game.id} className={`game-result ${game.winner_username ? 'completed' : 'tie'}`}>
                <div className="game-number">Game {index + 1}</div>
                <div className="game-outcome">
                  {game.winner_username ? (
                    <span className={game.winner_username === playerName ? 'winner' : 'loser'}>
                      {game.winner_username} won
                    </span>
                  ) : (
                    <span className="tie">Tie</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="actions">
          <button className="btn-home" onClick={handleReturnHome}>
            Return to lobby
          </button>
        </div>
      </div>
    </div>
  );
}

export default SeriesComplete;

